/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { CatalogDashboard } from "@app/api/trpc/routers/catalog";
import { money } from "./format";

type Props = { categories: CatalogDashboard["categories"] };

const LIMIT = 8;

// Sales per category, biggest first, as bars relative to the top one.
export const CategorySales = defineComponent<Props>({
  name: "CategorySales",
  props: ["categories"],
  setup(props) {
    const { t } = useLocalization();

    return () => {
      const selling = props.categories.filter((c) => c.revenue > 0);
      if (!selling.length) {
        return <p class="text-sm text-base-content/50">{t("catalog.overview.no_sales")}</p>;
      }
      const max = selling[0].revenue;
      const total = selling.reduce((s, c) => s + c.revenue, 0);
      return (
        <ul class="space-y-2.5">
          {selling.slice(0, LIMIT).map((c) => (
            <li key={c.id}>
              <div class="flex items-baseline justify-between gap-2 text-sm">
                <span class="truncate text-accent">{c.name}</span>
                <span class="whitespace-nowrap text-xs tabular-nums text-base-content/60">
                  {`${money(c.revenue)} · ${((c.revenue / total) * 100).toFixed(0)}%`}
                </span>
              </div>
              <div class="mt-1 h-1.5 overflow-hidden rounded-full bg-base-200">
                <div class="h-full rounded-full bg-primary" style={{ width: `${(c.revenue / max) * 100}%` }} />
              </div>
            </li>
          ))}
          {selling.length > LIMIT && (
            <li class="text-xs text-base-content/40">
              {t("catalog.overview.and_more").replace("{count}", String(selling.length - LIMIT))}
            </li>
          )}
        </ul>
      );
    };
  },
});
